import { ChangeDetectorRef, Component, EventEmitter, Input, Output, OnChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import {
  ConsentimientoCampo,
  ConsentimientoFinalidad,
  ConsentimientoSchema,
  CONSENTIMIENTO_SCHEMA_EJEMPLO,
} from '../../../core/models/consentimiento-schema.model';

/** Un fragmento del texto de aceptación: texto plano o un link a resolver. */
interface AcceptanceChunk {
  text: string;
  linkId: string | null;
  linkLabel: string | null;
  linkUrl: string | null;
}

/** Un fragmento de la descripción de una finalidad: texto plano o el link
 * embebido en linkedResource (ej. "listado" dentro de FIN-002). */
interface DescripcionChunk {
  text: string;
  url: string | null;
}

/** Forma del evento que emite el widget al enviar — se acerca al body real de
 * POST /consentimientos (cod_sociedad, cliente, finalidades[]), pero todavía
 * no incluye id_formulario/clave_idempotencia/aplica_al_grupo reales: eso lo
 * completa quien conecte el widget al backend (ver ConsentimientoApiService). */
export interface ConsentimientoWidgetSubmit {
  nombres: string;
  apellidos: string;
  rut: string;
  correo: string;
  telefono: string;
  finalidades: { codigo: string; otorgado: boolean }[];
  aceptaPoliticas: boolean;
}

@Component({
  selector: 'app-consentimiento-widget',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './consentimiento-widget.component.html',
  styleUrls: ['./consentimiento-widget.component.scss'],
})
export class ConsentimientoWidgetComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() schema: ConsentimientoSchema = CONSENTIMIENTO_SCHEMA_EJEMPLO;
  /** true mientras el padre está esperando la respuesta del POST real — deshabilita
   * el botón para evitar envíos duplicados (cada click genera un registro real). */
  @Input() enviando = false;

  /**
   * 4 colores en hexadecimal/rgb (09-sep-2026, a pedido explícito de Roberto:
   * "tienes un concepto errado — pides como parámetro el color texto, color
   * fondo, color botón, color texto botón, y el widget se pinta con esos
   * colores"). Este componente ya no sabe qué es "Kaufmann" ni "Andes Motor"
   * — solo recibe 4 colores y se pinta con ellos (ver bindings [style.--cw-*]
   * en el HTML). Los defaults son el look neutro original (el mismo que
   * tenía el widget antes de que existiera ningún concepto de marca).
   */
  @Input() colorTexto = '#ffffff';
  // #3C425073 = rgba(60, 66, 80, 0.45) en hex de 8 dígitos (#RRGGBBAA) — mismo
  // color/transparencia, solo en hexadecimal (09-sep-2026, a pedido explícito).
  @Input() colorFondo = '#3C425073';
  @Input() colorBoton = '#55606b';
  @Input() colorTextoBoton = '#ffffff';
  /** 09-sep-2026, pedido puntual solo para /web1consentimiento: la letra de
   * "Quiero ver más información" con el mismo gris que el placeholder de los
   * inputs (#9aa2ad). Default = el mismo valor fijo que tenía antes
   * (--cw-ink), así el resto de las páginas no cambia. Aparte de
   * colorTexto/colorFondo/colorBoton/colorTextoBoton porque esos 4 son los
   * que pidió Roberto explícitamente — este es un ajuste puntual, no parte
   * de esa lista. */
  @Input() colorMasInfo = '#1b2635';
  /** 09-sep-2026, prueba puntual: por ahora no se muestra en /consentimiento
   * (ver WidgetConsentimientoComponent) mientras se prueba el cierre
   * automático por inactividad — no se borró el botón ni cerrarWidget(),
   * solo se deja de renderizar cuando esto es false. */
  @Input() mostrarBotonCerrar = true;

  /**
   * 15-sep-2026, a pedido explícito (Roberto): si la página host ya conocía al cliente y se lo
   * pasó a GET /formularios/vigente, el padre (WidgetConsentimientoComponent) reenvía acá lo que
   * vino en la respuesta (Cliente) — se usa solo para precargar `valores` la primera vez que
   * cada campo se inicializa (ver ngOnChanges), nunca pisa lo que el titular ya haya escrito.
   */
  @Input() datosCliente: { rut?: string | null; nombres?: string | null; apellidos?: string | null; email?: string | null; telefono?: string | null } | null = null;
  @Output() cerrar = new EventEmitter<void>();
  @Output() enviar = new EventEmitter<ConsentimientoWidgetSubmit>();

  /** Valores de los campos de datos personales, indexados por id de campo. */
  valores: Record<string, string> = {};

  legalDetailOpen = false;
  accOpen: Record<'necesarias' | 'adicionales', boolean> = {
    necesarias: false,
    adicionales: false,
  };

  /** Qué finalidades adicionales están marcadas, por código. */
  finalidadesMarcadas: Record<string, boolean> = {};
  aceptaPoliticas = false;

  acceptanceChunks: AcceptanceChunk[] = [];

  mostrarExito = false;
  private timeoutExito?: ReturnType<typeof setTimeout>;

  ngOnChanges(): void {
    for (const campo of this.schema.camposPersonales) {
      if (this.valores[campo.id] === undefined) {
        this.valores[campo.id] = this.valorInicialDesdeCliente(campo.id) ?? '';
      }
    }
    for (const fin of this.schema.finalidadesAdicionales) {
      this.finalidadesMarcadas[fin.code] ??= false;
    }
    this.acceptanceChunks = this.resolverAcceptanceChunks();
  }

  /** Mapea el id de campo del schema (ver enviarFormulario()) al dato conocido del cliente,
   * si llegó alguno — ver docblock de @Input() datosCliente. */
  private valorInicialDesdeCliente(idCampo: string): string | null {
    if (!this.datosCliente) return null;
    switch (idCampo) {
      case 'nombre': return this.datosCliente.nombres ?? null;
      case 'apellido': return this.datosCliente.apellidos ?? null;
      case 'rut_cliente': return this.datosCliente.rut ?? null;
      case 'correo': return this.datosCliente.email ?? null;
      case 'telefono': return this.datosCliente.telefono ?? null;
      default: return null;
    }
  }

  /** El padre (WidgetConsentimientoComponent) llama esto directamente —vía
   * @ViewChild— cuando el POST /consentimientos responde con éxito. A pedido
   * explícito (28-ago-2026): avisar que se envió y dejar el formulario en
   * blanco de nuevo (antes no había ningún aviso en pantalla). Se llama
   * directo en vez de por @Input porque es más simple y no depende de que
   * Angular detecte el cambio de un input en el ciclo de change detection. */
  mostrarMensajeExitoYResetear(): void {
    this.mostrarExito = true;
    this.resetearFormulario();
    // El estado se actualiza bien, pero en el contexto standalone (Web Component)
    // Angular no siempre re-dibuja solo tras una mutación disparada por el padre
    // vía @ViewChild — forzamos el refresco explícitamente (verificado 28-ago-2026
    // con logs: mostrarExito/valores quedaban correctos mientras la pantalla no
    // cambiaba, hasta agregar este detectChanges()).
    this.cdr.detectChanges();
    if (this.timeoutExito) clearTimeout(this.timeoutExito);
    // 7s, no 3.5s: el envío en sí ya tarda ~9-10s (conexión a la base), así que
    // el mensaje necesita quedar visible un buen rato para que se alcance a ver.
    this.timeoutExito = setTimeout(() => {
      this.mostrarExito = false;
      this.cdr.detectChanges();
    }, 7000);
  }

  private resetearFormulario(): void {
    this.valores = {};
    for (const campo of this.schema.camposPersonales) {
      this.valores[campo.id] = '';
    }
    this.finalidadesMarcadas = {};
    for (const fin of this.schema.finalidadesAdicionales) {
      this.finalidadesMarcadas[fin.code] = false;
    }
    this.aceptaPoliticas = false;
    this.legalDetailOpen = false;
    this.accOpen = { necesarias: false, adicionales: false };
  }

  /** Agrupa camposPersonales en filas de a 2 usando la columna que declara el
   * schema (1 o 2) — a pedido explícito (28-ago-2026), el layout tiene que
   * salir del schema, no de un orden fijo en el código. Un campo empieza fila
   * nueva si su columna es menor o igual a la del campo anterior (ej. vuelve
   * a 1), o si el schema no trae columna y ya hay 2 en la fila actual. Una
   * fila con un solo campo queda marcada para ocupar el ancho completo. */
  get filasCampos(): ConsentimientoCampo[][] {
    const filas: ConsentimientoCampo[][] = [];
    let filaActual: ConsentimientoCampo[] = [];
    let ultimaColumna = 0;
    for (const campo of this.schema.camposPersonales) {
      const columna = campo.column ?? filaActual.length + 1;
      if (filaActual.length > 0 && (columna <= ultimaColumna || filaActual.length >= 2)) {
        filas.push(filaActual);
        filaActual = [];
      }
      filaActual.push(campo);
      ultimaColumna = columna;
    }
    if (filaActual.length > 0) filas.push(filaActual);
    return filas;
  }

  /** A pedido explícito (28-ago-2026): el botón se deshabilita si falta algún
   * campo obligatorio o no se aceptaron las políticas — antes solo lo bloqueaba
   * la validación nativa del navegador al hacer submit, sin ningún aviso visual
   * previo en el botón mismo. Las finalidades adicionales quedan afuera a
   * propósito: el propio helperText del schema dice que son opcionales. */
  get formularioValido(): boolean {
    const camposOk = this.schema.camposPersonales
      .filter((c) => c.required)
      .every((c) => (this.valores[c.id] ?? '').trim().length > 0);
    return camposOk && this.aceptaPoliticas;
  }

  get legalIntroResuelto(): string {
    return this.schema.legalIntro
      .replace('{{company}}', this.schema.company)
      .replace('{{brandAlias}}', this.schema.brandAlias);
  }

  /** A pedido explícito (25-sep-2026): si no hay finalidades adicionales cargadas, este
   * checkbox de "marcar todas" no debe salir marcado por defecto -- .every() sobre un arreglo
   * vacío da true matemáticamente, y eso hacía que el checkbox se viera tildado sin que el
   * titular marcara nada. */
  get todasLasFinalidadesMarcadas(): boolean {
    return this.schema.finalidadesAdicionales.length > 0
      && this.schema.finalidadesAdicionales.every((f) => this.finalidadesMarcadas[f.code]);
  }

  toggleLegalDetail(): void {
    this.legalDetailOpen = !this.legalDetailOpen;
  }

  toggleAcordeon(key: 'necesarias' | 'adicionales'): void {
    this.accOpen[key] = !this.accOpen[key];
  }

  toggleSeleccionarTodas(marcar: boolean): void {
    for (const fin of this.schema.finalidadesAdicionales) {
      this.finalidadesMarcadas[fin.code] = marcar;
    }
  }

  /** Parte la descripción de una finalidad en texto plano + el link de
   * linkedResource (si tiene), buscando ese label como substring literal
   * dentro de la descripción (ej. "listado" en la descripción de FIN-002). */
  descripcionChunks(fin: ConsentimientoFinalidad): DescripcionChunk[] {
    if (!fin.linkedResource) {
      return [{ text: fin.description, url: null }];
    }
    const { label, url } = fin.linkedResource;
    const idx = fin.description.indexOf(label);
    if (idx === -1) {
      return [{ text: fin.description, url: null }];
    }
    const antes = fin.description.slice(0, idx);
    const despues = fin.description.slice(idx + label.length);
    const chunks: DescripcionChunk[] = [];
    if (antes) chunks.push({ text: antes, url: null });
    chunks.push({ text: label, url: url ?? '#' });
    if (despues) chunks.push({ text: despues, url: null });
    return chunks;
  }

  cerrarWidget(): void {
    this.cerrar.emit();
  }

  enviarFormulario(): void {
    if (this.enviando) return;
    this.enviar.emit({
      nombres: this.valores['nombre'] ?? '',
      apellidos: this.valores['apellido'] ?? '',
      rut: this.valores['rut_cliente'] ?? '',
      correo: this.valores['correo'] ?? '',
      telefono: this.valores['telefono'] ?? '',
      finalidades: this.schema.finalidadesAdicionales.map((f) => ({
        codigo: f.code,
        otorgado: !!this.finalidadesMarcadas[f.code],
      })),
      aceptaPoliticas: this.aceptaPoliticas,
    });
  }

  /** Parte el texto de aceptación en fragmentos de texto plano y links,
   * resolviendo cada "{{link:id}}" contra schema.acceptanceLinks. */
  private resolverAcceptanceChunks(): AcceptanceChunk[] {
    const partes = this.schema.acceptanceText.split(/(\{\{link:[a-zA-Z0-9_]+\}\})/g);
    return partes
      .filter((p) => p.length > 0)
      .map((p) => {
        const match = p.match(/^\{\{link:([a-zA-Z0-9_]+)\}\}$/);
        if (!match) {
          return { text: p, linkId: null, linkLabel: null, linkUrl: null };
        }
        const linkId = match[1];
        const link = this.schema.acceptanceLinks.find((l) => l.id === linkId);
        return {
          text: '',
          linkId,
          linkLabel: link?.label ?? linkId,
          linkUrl: link?.url ?? null,
        };
      });
  }
}
